import {DndContext,closestCenter,KeyboardSensor,PointerSensor,useSensor,useSensors} from '@dnd-kit/core';
import {arrayMove,SortableContext,sortableKeyboardCoordinates,verticalListSortingStrategy,useSortable} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ReactComponent as DragFileIcon } from '../../assets/icons/DragFileIcon.svg';
import { ReactComponent as DragAreaIcon } from '../../assets/icons/DragAreaIcon.svg';
import { ReactComponent as UpArrowIcon } from '../../assets/icons/UpArrowIcon.svg';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/DownArrowIcon.svg';
import { ReactComponent as MoveIcon } from '../../assets/icons/MoveIcon.svg';

const SortProvider = ({ id, providerName, children }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id });
    return (
        <div ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition: isDragging ? "none" : "transform 200ms ease", // Removes flickering
                background: isDragging ? "#d1e0fc" : "#edf3fc", // Highlight dragged item
                padding: "15px",
                marginBottom: "10px",
                borderRadius: "5px",
                opacity: isDragging ? 0.6 : 1 // Reduce opacity while dragging
            }}
            {...attributes}
            {...listeners} className='report-section mb-4'>
            <div className="d-flex gap-4">
                <span className="move-icon">
                    <DragAreaIcon />
                </span>
                <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="report-btn w-100">
                            <div className="d-flex align-items-center gap-2">
                                <span className="card-title">{providerName}</span>
                            </div>
                        </div>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
};

const SortFile = ({ id, entry }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'grab',
    };

    return (

        <div ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners} className="report-item">
            <div className="d-flex align-items-center">
                {/* move icon */}
                <span className="me-2">
                    <DragFileIcon />
                </span>
                {/* move icon */}
                <MoveIcon className='me-2' />
                {entry}
            </div>
            {/* arrow icons */}
            <div>
                <span>
                    <DownArrowIcon />
                </span>
                <span className="ms-2">
                    <UpArrowIcon />
                </span>
            </div>
        </div>

    );
};

const ReorderMedicalProvider = ({providerType,heading,setFieldValue,formikValues,selectedTiffIndex,isShowReorderSec,setShowReorderSec}) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = (event) => {
        const medicalFiles = formikValues?.medicalFiles[selectedTiffIndex]?.[providerType].medicalRecords;
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = medicalFiles.findIndex(item => item.providerName === active.id?.providerName);
        const newIndex = medicalFiles.findIndex(item => item.providerName === over.id?.providerName);
        if (oldIndex === -1 || newIndex === -1) return;
        const updatedFiles = arrayMove(medicalFiles, oldIndex, newIndex);
        setFieldValue(`medicalFiles[${selectedTiffIndex}].[${providerType}].medicalRecords`, updatedFiles);
    };

    const handleFileDragEnd = (providerId) => (event) => {
        const medicalFiles = formikValues?.medicalFiles[selectedTiffIndex]?.[providerType].medicalRecords;
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const updatedProviders = medicalFiles.map((provider) => {
            if (provider.providerName === providerId) {
                const oldIndex = provider.source.findIndex(file => file.name === active.id);
                const newIndex = provider.source.findIndex(file => file.name === over.id);
                if (oldIndex === -1 || newIndex === -1) return provider;
                return {
                    ...provider,
                    source: arrayMove(provider.source, oldIndex, newIndex),
                };
            }
            return provider;
        });

        setFieldValue(`medicalFiles[${selectedTiffIndex}].[${providerType}].medicalRecords`, updatedProviders);
    };

  return (
    <>
        {formikValues?.medicalFiles[selectedTiffIndex]?.[providerType].medicalRecords?.length > 0 && isShowReorderSec && <div>
            <div className='d-flex justify-content-between align-items-center btm-btns mb-4'>
                <h3>{heading}</h3>
                <div className="btns text-center button-spacing">
                    <button className="btn btn-theme btn-border" type="button" onClick={() => setShowReorderSec((preVal=>({...preVal,[providerType]:false})))}>Cancel</button>
                    <button className="btn btn-theme" type="button" onClick={() => setShowReorderSec((preVal=>({...preVal,[providerType]:false})))}>Save</button>
                </div>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={formikValues?.medicalFiles[selectedTiffIndex]?.[providerType].medicalRecords}
                    strategy={verticalListSortingStrategy}
                >
                    {formikValues?.medicalFiles[selectedTiffIndex]?.[providerType].medicalRecords?.map((provider, index) => (
                        <SortProvider key={index} id={provider} providerName={provider.providerName}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleFileDragEnd(provider.providerName,index)}
                            >
                                <SortableContext
                                    items={provider.source.map(file => file.name)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {provider?.source.map((file, index) => (
                                        <SortFile key={file.name} id={file.name} entry={file.name} />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </SortProvider>
                    ))}
                </SortableContext>
            </DndContext>
            <div className='d-flex justify-content-end align-items-center btm-btns button-spacing'>
                <button className="btn btn-theme btn-border" type="button" onClick={() => setShowReorderSec((preVal=>({...preVal,[providerType]:false})))}>Cancel</button>
                <button className="btn btn-theme" type="button" onClick={() => setShowReorderSec((preVal=>({...preVal,[providerType]:false})))}>Save</button>
            </div>
        </div>}
    </>
  )
}

export default ReorderMedicalProvider